// src/utils/lossRunGrid.js
//
// The coverage grid at the top of the Loss Runs tab: rows are lines of
// coverage, columns the most recent policy terms (oldest left), each cell the
// sources holding a policy for that line and term, colored per POLICY by the
// same rules as the cards (policyState) and rolled up to the worst.
//
// Pure. Blank is unknown; Submitted/Quoted rows are not policies.

import {
  dateOnlyOf, isLossRunEligible, termOf, policyState, worstColor, boolOrNull, UNASSIGNED_ID,
} from './lossRunStatus';
import { cellOwnership, eraBands, agencyTag } from './lossRunEra';

// ── Need tags ────────────────────────────────────────────────────────────────
// GENERAL PRACTICE, EDITABLE. What a new carrier or wholesaler usually wants
// before quoting, by line of business. Keys are compared trimmed and
// case-insensitive against FIG_Policies.LOB. A LOB not listed here is
// "Not classified" and shows up in the tab as such — add it here when you
// know how it is treated.
export const NEED_KEYS = ['required', 'letter', 'notUsual', 'unclassified'];
export const NEED_LABELS = {
  required: 'Loss runs required',
  letter: 'Letter usually accepted',
  notUsual: 'Not usually requested',
  unclassified: 'Not classified',
};
export const LOSS_RUN_NEED_BY_LOB = {
  // currently valued loss runs are expected for these
  'commercial general liability': 'required',
  'general liability': 'required',
  'commercial auto': 'required',
  'business auto': 'required',
  'workers compensation': 'required',
  "workers' compensation": 'required',
  'commercial property': 'required',
  'property': 'required',
  'stock throughput': 'required',
  // a signed no-known-loss letter is usually accepted for these
  'umbrella': 'letter',
  'commercial umbrella': 'letter',
  'excess liability': 'letter',
  'excess': 'letter',
  // rarely asked for at placement
  'equipment breakdown': 'notUsual',
  'foreign liability': 'notUsual',
  'inland marine': 'notUsual',
  'employment practices liability': 'notUsual',
  'pollution liability': 'notUsual',
  'cyber liability': 'notUsual',
  'crime': 'notUsual',
  'directors & officers': 'notUsual',
  'fiduciary liability': 'notUsual',
};

// One chip = one source in one cell (line × term). Two chips of the same
// source in different cells must never share a key (the popover open state
// is a single chip key).
export function chipKey(lob, term, sourceId) {
  return `${String(lob ?? '')}|${String(term ?? '')}|${String(sourceId ?? '')}`;
}

/**
 * placePopover({ anchor, size, viewport, gap })
 *   anchor: the chip's bounding rect { top, left, bottom, right, width, height }
 *   size:   the popover's { width, height }
 * Below-left of the chip by default; flips to align right when it would run
 * off the right edge, and above the chip when it would run off the bottom.
 * Clamped to the viewport with a small margin. Pure; used with position: fixed.
 */
export function placePopover({ anchor, size, viewport, gap = 4, margin = 8 } = {}) {
  const vw = viewport?.width ?? 0, vh = viewport?.height ?? 0;
  const w = size?.width ?? 0, h = size?.height ?? 0;
  let left = anchor.left;
  let flipX = false, flipY = false;
  if (left + w > vw - margin) { left = anchor.right - w; flipX = true; }
  if (left < margin) left = margin;
  let top = anchor.bottom + gap;
  if (top + h > vh - margin) {
    const above = anchor.top - gap - h;
    if (above >= margin) { top = above; flipY = true; }
    else top = Math.max(margin, vh - margin - h);
  }
  return { top, left, flipX, flipY };
}

export function needForLob(lob) {
  const key = String(lob ?? '').trim().toLowerCase();
  return LOSS_RUN_NEED_BY_LOB[key] || 'unclassified';
}

const blank = v => v === null || v === undefined || String(v).trim() === '';
const idOf = v => (blank(v) ? UNASSIGNED_ID : String(v).trim());

/**
 * buildCoverageGrid({ sources, policies, requests, today, maxTerms = 5 })
 * → {
 *     terms:  ['2022-23', ..., '2026-27']           // oldest left, at most maxTerms
 *     eras:   [{ key, name, label, own, terms, span }]  // consecutive terms by dominant AgencyOfRecord (lossRunEra)
 *     rows:   [{ lob, need, needLabel, cells: { [term]: cell | null } }]  // sorted by need
 *     counts: { green, yellow, red, grey }          // required-line cells only
 *   }
 * cell = { term, lob, color, policies: [...], ownership, agencyTag, chips: [{ sourceId, name, kind, count, color, policyIds, doNotContact, reasons, cellOwnership }] }
 *   ownership — lossRunEra.cellOwnership: mine / notMine / mixed (never empty here; an empty cell is null)
 *   agencyTag — the exception tag ("Texas First Insurance" in a Fragos column) or null
 */
export function buildCoverageGrid({ sources = [], policies = [], requests = [], today, maxTerms = 5, handled } = {}) {
  // handled: optional Map(policyId -> { handled, stillOpen, sentence }) from
  // lossRunResolutions.resolutionIndex. Never changes a color; only annotates.
  const handledOf = id => handled?.get?.(String(id)) || null;
  const todayKey = dateOnlyOf(today) || dateOnlyOf(new Date());
  const byId = new Map((sources || []).map(s => [String(s.id), s]));
  const eligible = (policies || []).filter(isLossRunEligible);

  // Columns: the most recent terms, oldest on the left.
  const allTerms = [...new Set(eligible.map(p => termOf(p)).filter(Boolean))].sort();
  const terms = allTerms.slice(-maxTerms);
  const termSet = new Set(terms);

  // Rows: one per LOB (blank LOB -> "Line not on record").
  const byLob = new Map();
  const byTerm = new Map();
  for (const p of eligible) {
    const t = termOf(p);
    if (!t || !termSet.has(t)) continue;
    const lob = blank(p.LOB) ? 'Line not on record' : String(p.LOB).trim();
    if (!byLob.has(lob)) byLob.set(lob, new Map());
    const perTerm = byLob.get(lob);
    if (!perTerm.has(t)) perTerm.set(t, []);
    perTerm.get(t).push(p);
    if (!byTerm.has(t)) byTerm.set(t, []);
    byTerm.get(t).push(p);
  }
  // Era band per column: the dominant AgencyOfRecord of each term, merged
  // across consecutive terms. Cells compare against their column's band.
  const eras = eraBands(terms, byTerm);
  const bandOf = new Map();
  for (const e of eras) for (const t of e.terms) bandOf.set(t, e);

  const stateOf = p => {
    const sid = idOf(p.LossRunSourceId);
    const source = sid === UNASSIGNED_ID ? null : byId.get(sid) || null;
    return policyState({ policy: p, source, requests, today: todayKey, unassigned: sid === UNASSIGNED_ID });
  };

  const rows = [...byLob.entries()].map(([lob, perTerm]) => {
    const need = needForLob(lob);
    const cells = {};
    for (const term of terms) {
      const list = perTerm.get(term);
      if (!list || list.length === 0) { cells[term] = null; continue; }
      const ownership = cellOwnership(list);
      const bySource = new Map();
      for (const p of list) {
        const sid = idOf(p.LossRunSourceId);
        if (!bySource.has(sid)) bySource.set(sid, []);
        bySource.get(sid).push(p);
      }
      const chips = [...bySource.entries()].map(([sid, pols]) => {
        const source = sid === UNASSIGNED_ID ? null : byId.get(sid) || null;
        const states = pols.map(stateOf);
        const colorCounts = { green: 0, yellow: 0, red: 0, grey: 0 };
        for (const st of states) if (st.color in colorCounts) colorCounts[st.color]++;
        const onFile = colorCounts.green + colorCounts.yellow;   // a run or letter is on file (current or stale)
        const mixed = Object.values(colorCounts).filter(n => n > 0).length > 1;
        const name = sid === UNASSIGNED_ID ? 'Unassigned' : (source?.Title || `Source #${sid}`);
        const key = chipKey(lob, term, sid);
        // Resolutions: how many of the NON-GREEN policies are handled this season.
        const nonGreen = pols.filter((p, i) => states[i].color !== 'green');
        const handledPols = nonGreen.filter(p => handledOf(p.id)?.handled);
        const check = nonGreen.length === 0 ? null : handledPols.length === nonGreen.length ? 'all' : handledPols.length > 0 ? 'some' : null;
        const handledSentences = [...new Set(nonGreen.map(p => handledOf(p.id)).filter(h => h && (h.handled || h.stillOpen)).map(h => h.sentence))];
        return {
          key,
          term,
          lob,
          nonGreen: nonGreen.length,
          handledCount: handledPols.length,
          check,
          handledLabel: check === 'all' ? '✓' : check === 'some' ? `✓ ${handledPols.length} of ${nonGreen.length} handled` : '',
          handledTooltip: handledSentences.join(' | '),
          sourceId: sid,
          name,
          kind: source?.Kind ?? '',
          count: pols.length,
          color: worstColor(states.map(s => s.color)) || 'red',
          colorCounts,
          onFile,
          mixed,
          // "RT Specialty · 5 of 13 on file" when colors differ; "· 13 policies" when they agree
          label: mixed ? `${name} · ${onFile} of ${pols.length} on file` : (pols.length > 1 ? `${name} · ${pols.length} policies` : name),
          tooltip: ['green', 'yellow', 'red', 'grey'].filter(c => colorCounts[c]).map(c => `${colorCounts[c]} ${c}`).join(' · '),
          reasons: [...new Set(states.flatMap(s => s.reasons))],
          policyIds: pols.map(p => String(p.id)),
          doNotContact: source ? boolOrNull(source.DoNotContact) === true : false,
          cellOwnership: ownership,
        };
      }).sort((a, b) => a.name.localeCompare(b.name));
      cells[term] = { term, lob, color: worstColor(chips.map(c => c.color)) || 'red', policies: list, chips, ownership, agencyTag: agencyTag(ownership, bandOf.get(term)) };
    }
    return { lob, need, needLabel: NEED_LABELS[need], cells };
  });

  rows.sort((a, b) => NEED_KEYS.indexOf(a.need) - NEED_KEYS.indexOf(b.need) || a.lob.localeCompare(b.lob));

  const counts = { green: 0, yellow: 0, red: 0, grey: 0 };
  for (const r of rows) {
    if (r.need !== 'required') continue;
    for (const term of terms) { const c = r.cells[term]; if (c && c.color in counts) counts[c.color]++; }
  }

  return { terms, eras, rows, counts };
}

// Every distinct LOB in the data with its tag — for reporting and the UI legend.
export function lobNeedList(policies) {
  const lobs = [...new Set((policies || []).filter(isLossRunEligible).map(p => String(p.LOB ?? '').trim()).filter(Boolean))].sort();
  return lobs.map(lob => ({ lob, need: needForLob(lob), needLabel: NEED_LABELS[needForLob(lob)] }));
}
